<!-- eslint-disable max-len -->
<style scoped>
  .project-concept-button {
    position:absolute;
    top:0;
    right:0;
  }
  .project-concept-title-margin {
    margin-top: 20px;
  }
  .project-concept-body-margin {
    margin-bottom: 10px;
  }
  .project-subwatershed {
    list-style: none;
    margin-bottom: 0px;
    padding-inline-start: 0px;
  }
</style>
<template>
  <div id="project-details">
    <div class="section-title">Project Details</div>
    <div v-if="project" class="card">
      <div class="card-body">
        <div class="row details-row">
          <span class="details-label">Project Name:</span>
          <span>
            <a  v-if='project'
                :href='`${config.insightly.projectUrl}${project.id}`'
                target='insightly'
            >{{project.projectName}}</a>
          </span>
        </div>
        <div class="row details-row">
          <span class="details-label">Project ID:</span> {{ project.projectId }}
        </div>
        <div class="row details-row">
          <span class="details-label">Project Status: </span>
          <span>{{ project.status }}</span>
        </div>
        <div class="row details-row">
          <span class="details-label">Approval Status: </span>
          <span>{{ project.approvalStatus }}</span>
        </div>
        <div class="row details-row">
          <span class="details-label">Project Manager: </span>
          <span v-if='project.projectManager'>
            <span v-if='project.projectManager.firstName'>
              <span>{{`${project.projectManager.firstName}`}}</span>
            </span>
            <span v-if='project.projectManager.lastName'>
              <span> {{`${project.projectManager.lastName}`}}</span>
            </span>
          </span>
        </div>
        <div class="row details-row">
          <span class="details-label">Primary Goal: </span>
          <span>{{ project.primaryGoal }}</span>
        </div>
        <div class="row details-row">
          <span class="details-label">Program: </span>
          <span>{{ project.category }}</span>
        </div>
        <div class="row details-row">
          <span class="details-label">Contract Acres: </span>
          <span>{{ project.contractAcres }}</span>
        </div>
        <div class="row details-row">
          <span class="details-label">Instream Lease Number: </span>
          <span>{{ project.instreamLeaseNumber | DefaultStringIfNull }}</span>
        </div>
        <div class="row details-row">
          <span class="details-label">Start Date: </span>
          <span>{{ startDate }}</span>
        </div>
        <div class="row details-row">
          <span class="details-label">End Date: </span>
          <span>{{ endDate }}</span>
        </div>
        <div class="row details-row">
          <span class="details-label">Watershed Name: </span>
          <span v-if="project.watershedName">{{project.watershedName}}</span>
          <span v-else>N/A</span>
        </div>
        <div class="row details-row">
          <div class="details-label">Subwatershed Name: </div>
          <ul v-if="project.subwatershedNames && project.subwatershedNames.length > 0 && project.subwatershedNames[0] != ''" class='project-subwatershed'>
            <li v-for="subwatershedName in project.subwatershedNames" :key="subwatershedName">{{subwatershedName}}</li>
          </ul>
          <span v-else>N/A</span>
        </div>
      </div>
    </div>
    <div class="section-title project-concept-title-margin">
      <span>Project Concept</span>
    </div>
    <b-overlay :show="formLoading">
      <div v-if='projectConcept' class="card card-body project-concept-body-margin">
        <b-button v-if="isEditable" size="sm" variant='outline' class='project-concept-button' @click='onEditProjectConceptButtonClick'>
          <b-icon icon="pencil-square" aria-hidden="true"></b-icon>
        </b-button>
        <div class='row details-row'>
          <div class='details-label'>Resource Priorities:</div>
          <span v-if='projectConcept && projectConceptResourcePriorities && projectConceptResourcePriorities.length > 0'>
            <div v-for="item in projectConceptResourcePriorities" :key="item.id">
              {{ item.name }}
            </div>
          </span>
          <span v-else>N/A</span>
        </div>
        <div class='row details-row'>
          <div class='details-label'>Ranking Score:</div>
          <span v-if='projectConcept && projectConcept.rankingScore'>{{ projectConcept.rankingScore }}</span>
          <span v-else>N/A</span>
        </div>
        <div class='row details-row'>
          <div class='details-label'>Estimated Project Cost:</div>
          <span v-if='projectConcept && projectConcept.estimatedCost != null'>{{ projectConcept.estimatedCost | toCurrency }}</span>
          <span v-else>N/A</span>
        </div>
        <div class='row details-row'>
          <div class='details-label'>Project Concept Statement:</div>
          <span v-if='projectConcept && projectConcept.statement'>{{ projectConcept.statement }}</span>
          <span v-else>N/A</span>
        </div>
          <b-modal id="editProjectConceptModal" title="Edit Project Concept" size="xl">
            <p>
              <VueFormGenerator :schema="formSchema" :model="projectConceptModel" :options="formOptions" @validated="onValidated"/>
            </p>
            <template v-slot:modal-footer>
              <b-button size="sm" variant="danger" @click="onEditProjectConceptButtonCancelClick" class='btn-danger'>
                <span>Cancel</span>
              </b-button>
              <b-button :disabled="!formValid" size="sm" variant="success" @click="onEditProjectConceptButtonUpdateClick" class='bnt-success'>
                <span>Save</span>
              </b-button>
            </template>
          </b-modal>
      </div>
    </b-overlay>
  </div>
</template>
<script>
/* eslint-disable max-len, padded-blocks, prefer-destructuring */
import { mapState } from 'vuex';
import moment from 'moment';
import ProjectSchemaHelper from '../../mixins/projectSchemaHelper';

export default {
  name: 'ProjectDetails',
  mixins: [ProjectSchemaHelper],
  computed: {
    ...mapState(['config']),
    ...mapState('Membership', {
      memberOf: (state) => state.memberOf,
    }),
    ...mapState('Project', {
      project: (state) => state.project,
      projectConcept: (state) => state.projectConcept,
      projectResourcePriorities: (state) => state.projectResourcePriorities,
    }),
    isEditable() {
      if (!this.config || !this.memberOf || this.memberOf.length === 0 || !this.project || !this.project.approvalStatus) return false;
      const submitStatus = this.config.projectOverviewPage.submitStatus;
      switch (this.project.approvalStatus) {
        case submitStatus.ConceptRejected:
          return this.memberOf.filter((item) => this.config.projectOverviewPage.submitterRoles.includes(item.displayName)).length > 0;
        case submitStatus.Draft:
          return this.memberOf.filter((item) => this.config.projectOverviewPage.submitterRoles.includes(item.displayName)).length > 0;
        case submitStatus.SubmittedForPlanningApproval:
          return this.memberOf.filter((item) => this.config.projectOverviewPage.approverRoles.includes(item.displayName)).length > 0;
        default:
          return false;
      }
    },
    startDate() {
      return moment(String(this.project.startDate)).format('MM/DD/YYYY');
    },
    endDate() {
      return moment(String(this.project.endDate)).format('MM/DD/YYYY');
    },
  },
  data() {
    return {
      formLoading: false,
      formOptions: { validateAfterChanged: true },
      formSchema: {},
      formValid: false,
      projectConceptModel: {},
      projectConceptResourcePriorities: [],
    };
  },
  mounted() {
    if (this.project != null) {
      this.loadProjectConcept();
    } else {
      const unsubscribe = this.$store.subscribe((mutation) => {
        if (mutation.type === 'Project/SET_PROJECT') {
          unsubscribe();
          this.loadProjectConcept();
        }
      });
    }
  },
  methods: {
    loadProjectConcept() {
      (async () => {
        await this.$store.dispatch('Project/getProjectConcept', parseInt(this.$route.params.id, 10));
        if (!this.projectConcept || this.projectConcept === null) {
          await this.$store.dispatch('Project/postProjectConcept', { projectId: parseInt(this.$route.params.id, 10) });
          await this.$store.dispatch('Project/getProjectConcept', parseInt(this.$route.params.id, 10));
        }
        await this.$store.dispatch('Project/getProjectResourcePriorities');
        this.projectConceptResourcePriorities = this.projectConcept.projectConceptProjectResourcePriorities.map((e) => e.projectResourcePriority).sort((a, b) => {
          if (a.sortOrder < b.sortOrder) return -1;
          if (a.sortOrder === b.sortOrder) return 0;
          return 1;
        });
        this.formSchema = this.getConceptSchema();
      })();
    },
    onEditProjectConceptButtonClick() {
      this.projectConceptModel = { ...this.projectConcept };
      this.projectConceptModel.resourcePriorityIds = this.projectConceptResourcePriorities.map((e) => e.id);
      this.$bvModal.show('editProjectConceptModal');
      this.validateRequiredFields([]);
    },
    onEditProjectConceptButtonCancelClick() {
      this.$bvModal.hide('editProjectConceptModal');
    },
    onEditProjectConceptButtonUpdateClick() {
      (async () => {
        this.formLoading = true;
        this.$bvModal.hide('editProjectConceptModal');
        this.projectConceptModel.projectConceptProjectResourcePriorities = this.projectConceptModel.resourcePriorityIds.map((e) => ({ projectConceptId: this.project.id, projectResourcePriorityId: e }));
        await this.$store.dispatch('Project/putProjectConcept', this.projectConceptModel);
        await this.$store.dispatch('Project/getProjectConcept', this.project.id);
        await this.$store.dispatch('Project/getProjectResourcePriorities');
        this.projectConceptResourcePriorities = this.projectConcept.projectConceptProjectResourcePriorities.map((e) => e.projectResourcePriority).sort((a, b) => {
          if (a.sortOrder < b.sortOrder) return -1;
          if (a.sortOrder === b.sortOrder) return 0;
          return 1;
        });
        this.formLoading = false;
      })();
    },
    onValidated(isValid, errors) {
      this.formValid = isValid;
      this.validateRequiredFields(errors || []);
    },
    validateRequiredFields(errors) {
      this.formValid = true;
      if (!this.projectConceptModel.resourcePriorityIds || this.projectConceptModel.resourcePriorityIds.length === 0) this.formValid = false;
      if (this.projectConceptModel.estimatedCost == null || this.projectConceptModel.estimatedCost < 0) this.formValid = false;
      if (!this.projectConceptModel.statement || this.projectConceptModel.statement == null || this.projectConceptModel.statement.length < 50) this.formValid = false;
      if (errors && errors.length > 0) this.formValid = false;
    },
  },
};
</script>
